import { useState, useEffect } from 'react';
import { FaFileAlt } from 'react-icons/fa';
import AppModal from '../../ModalSystem/AppModal';
import api from '../../../api';
import { useI18n } from '../../../i18n';
import './EmailDetailModal.css';

const sanitizeEmailHtml = (html) => {
    if (!html) return '';
    let safe = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    safe = safe.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
    safe = safe.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '');
    safe = safe.replace(/(href|src|action)\s*=\s*["']?\s*javascript:[^"'>]*/gi, '$1="#"');
    safe = safe.replace(/src\s*=\s*["']?\s*data:[^"'>]*/gi, 'src=""');
    return safe;
};

const getInitials = (name, email) => {
    if (name) return name.split(' ').map((part) => part[0]).join('').toUpperCase().slice(0, 2);
    return email ? email[0].toUpperCase() : '?';
};

const formatRecipients = (list, fallback) => {
    if (!list?.length) return fallback;
    return list.map((recipient) => recipient.name || recipient.email).join(', ');
};

const formatDateTime = (dateStr, locale) => {
    if (!dateStr) return '';
    const localeCode = locale === 'en' ? 'en-GB' : (locale || 'en');
    return new Date(dateStr).toLocaleString(localeCode, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const EmailDetailModal = ({ emailId, onClose }) => {
    const { locale, t } = useI18n();
    const [email, setEmail] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!emailId) return;

        setLoading(true);
        setEmail(null);

        api.get(`/mail/emails/${emailId}`)
            .then((res) => {
                if (res.status) {
                    const emailData = res.data.email;
                    setEmail(emailData);

                    if (emailData && !emailData.is_read) {
                        api.post(`/mail/emails/${emailId}/read`, { is_read: true }).catch(() => {});
                    }
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [emailId]);

    return (
        <AppModal
            isOpen={!!emailId}
            onClose={onClose}
            title={email?.subject || (loading ? t('common.status.loading') : t('legacy.no_subject'))}
            size="lg"
        >
            {loading ? (
                <div className="emd-loading">
                    <div className="emd-spinner" />
                </div>
            ) : !email ? (
                <div className="emd-loading">{t('common.errors.failed_load_details')}</div>
            ) : (
                <>
                    <div className="emd-meta">
                        <div className="emd-avatar">
                            {getInitials(email.from?.name, email.from?.email)}
                        </div>
                        <div className="emd-info">
                            <div className="emd-sender-row">
                                <span className="emd-sender-name">
                                    {email.from?.name || email.from?.email}
                                </span>
                                {email.from?.name && (
                                    <span className="emd-sender-email">
                                        &lt;{email.from?.email}&gt;
                                    </span>
                                )}
                            </div>
                            <div className="emd-recipients">
                                <span>{t('legacy.to_label')} {formatRecipients(email.to, t('common.misc.na'))}</span>
                                {email.cc?.length > 0 && (
                                    <span> &nbsp;|&nbsp; {t('legacy.cc_label')}: {formatRecipients(email.cc, t('common.misc.na'))}</span>
                                )}
                            </div>
                        </div>
                        <div className="emd-date">{formatDateTime(email.date, locale)}</div>
                    </div>

                    <div className="emd-divider" />

                    <div className="emd-body">
                        <div
                            className="emd-body-content"
                            dangerouslySetInnerHTML={{
                                __html: sanitizeEmailHtml(
                                    email.body_html ||
                                    email.body_text?.replace(/\n/g, '<br>') ||
                                    `<em style="color:#94a3b8">${t('common.empty_states.no_content')}</em>`
                                ),
                            }}
                        />
                    </div>

                    {email.attachments?.filter((attachment) => attachment.disposition !== 'inline').length > 0 && (
                        <div className="emd-attachments">
                            <div className="emd-attachments-title">
                                {t('legacy.attachments_label')}&nbsp;
                                ({email.attachments.filter((attachment) => attachment.disposition !== 'inline').length})
                            </div>
                            <div className="emd-attachment-list">
                                {email.attachments
                                    .filter((attachment) => attachment.disposition !== 'inline')
                                    .map((attachment) => (
                                        <a
                                            key={attachment.id}
                                            href={attachment.download_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="emd-attachment-item"
                                        >
                                            <FaFileAlt className="emd-attach-icon" />
                                            <span className="emd-attach-name">{attachment.filename}</span>
                                            <span className="emd-attach-size">{attachment.human_size}</span>
                                        </a>
                                    ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </AppModal>
    );
};

export default EmailDetailModal;
